import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/primitives';
import { useAuth } from '../auth/AuthContext';
import { marketingDocs, marketingGroups } from './documents';
import { Icon, type IconName } from '../../components/icons/Icon';
import { SupportQR } from '../../components/SupportQR';
import { InstallGuideButton } from './InstallGuide';
import { isStandalone } from '../../lib/installPrompt';
import { ThemeGuideButton } from './ThemeGuide';
import styles from './MarketingLanding.module.css';

const features: { icon: IconName; label: string; head: string; text: string }[] = [
  {
    icon: 'layers',
    label: 'rychle',
    head: 'Stavba, firmy a šablony',
    text: 'Jednou vyplněná stavba se propisuje do všech dokumentů. Firmy z ARES, vlastní fakturační profily a logo na hlavičce.',
  },
  {
    icon: 'printer',
    label: 'profesionálně',
    head: 'Čistý tisk i PDF e-mailem',
    text: 'Dokumenty odpovídají stavební praxi. Hotové PDF pošlete investorovi přímo z aplikace, uložené zůstane v archivu.',
  },
  {
    icon: 'smartphone',
    label: 'kdekoli',
    head: 'Mobil i kancelář',
    text: 'Aplikace se nainstaluje do telefonu i počítače. Na stavbě stačí mobil, v kanceláři plný formulář.',
  },
  {
    icon: 'signature',
    label: 'podpisy',
    head: 'Podpis bez tiskárny',
    text: 'Knihovna podpisů, podpis prstem na displeji a podepsání PDF, JPG, Wordu i Excelu.',
  },
  {
    icon: 'share',
    label: 'spolupráce',
    head: 'Sdílení a schvalování',
    text: 'Úkoly kolegům, sdílené kontrolní dny a projektová dokumentace, schvalování košilek, kontrola vyúčtování účetní.',
  },
  {
    icon: 'save',
    label: 'bez ztrát',
    head: 'Automatické ukládání',
    text: 'Rozepsaný dokument se průběžně ukládá. Nové převody ve vyúčtování ohlásí e-mail i notifikace, obnovu OOPP hlídá odznak v menu.',
  },
  {
    icon: 'contrast',
    label: 'vzhled',
    head: 'Tmavý, nebo světlý',
    text: 'Aplikaci přepnete do světlého režimu a dokumenty na čistě bílé i v PDF. Najdete v Nastavení účtu → Vzhled.',
  },
];

export function MarketingLanding() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const year = new Date().getFullYear();

  const enterApp = () => navigate(isAuthenticated ? '/app' : '/login');

  // Odkaz z e-mailu pro obnovu hesla (?reset_token=…) přesměruj na reset formulář.
  const resetToken = searchParams.get('reset_token');
  if (resetToken) {
    return <Navigate to={`/reset-password?token=${encodeURIComponent(resetToken)}`} replace />;
  }

  return (
    <div className={styles.page}>
      <nav className={styles.nav}>
        <Link to="/" className={styles.logo}>
          <div className={styles.logoMark}>S</div>
          <div className={styles.logoText}>
            <span className={styles.logoName}>sitedoc.cz</span>
            <span className={styles.logoSub}>Stavební dokumentace</span>
          </div>
        </Link>
        <div className={styles.navActions}>
          {isAuthenticated ? (
            <Button variant="primary" onClick={enterApp}>
              Do aplikace →
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => navigate('/login')}>
              Přihlásit se →
            </Button>
          )}
        </div>
      </nav>

      <section className={styles.hero}>
        <div className={styles.eyebrow}>Stavební dokumentace online</div>
        <h1 className={styles.title}>
          Generátor stavební<br />
          <span className={styles.accent}>dokumentace</span>
        </h1>
        <p className={styles.subtitle}>
          Vyplňte základní údaje o stavbě a okamžitě získejte profesionální dokumenty připravené
          k&nbsp;tisku nebo odeslání. Předávací protokoly, faktury s&nbsp;QR platbou, stavební deník,
          kontrolní dny, BOZP, podpisy, úkoly i&nbsp;vyúčtování — vše v&nbsp;jedné aplikaci.
        </p>
        <div className={styles.cta}>
          <Button variant="primary" size="lg" onClick={enterApp}>
            {isAuthenticated ? 'Pokračovat v aplikaci' : 'Vstoupit do aplikace'}
          </Button>
          {!isAuthenticated && (
            <Button variant="secondary" size="lg" onClick={() => navigate('/register')}>
              Registrovat se zdarma
            </Button>
          )}
        </div>
        <div className={styles.ctaHint}>
          <strong>✓ Registrace zdarma</strong>
          {/* V nainstalované aplikaci (PWA / Google Play) „bez instalace" nedává smysl */}
          {!isStandalone() && <> · Žádné instalace · Funguje v&nbsp;prohlížeči</>}
        </div>
        <div className={styles.guides}>
          <InstallGuideButton />
          <ThemeGuideButton />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.featureStrip}>
          {features.map((f, i) => (
            <div key={f.head} className={styles.feature}>
              <div className={styles.featureIcon}>
                <Icon name={f.icon} size={20} />
              </div>
              <div>
                <div className={styles.featureLabel}>
                  {String(i + 1).padStart(2, '0')} · {f.label}
                </div>
                <div className={styles.featureHead}>{f.head}</div>
                <div className={styles.featureText}>{f.text}</div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.sectionTitle}>
          <div className={styles.sectionHead}>Co aplikace umí</div>
          <div className={styles.sectionNote}>
            {marketingDocs.length}&nbsp;modulů — vše v&nbsp;češtině, dokumenty ve formátu A4
          </div>
        </div>

        {marketingGroups.map((g) => {
          const items = marketingDocs.filter((d) => d.group === g.id);
          if (items.length === 0) return null;
          return (
            <div key={g.id} className={styles.group}>
              <div className={styles.groupHead}>{g.label}</div>
              <div className={styles.tiles}>
                {items.map((d) => (
                  <div key={d.name} className={styles.tile}>
                    <div className={styles.tileIcon}>
                      <Icon name={d.icon} size={19} />
                    </div>
                    <div className={styles.tileBody}>
                      <div className={styles.tileName}>{d.name}</div>
                      <div className={styles.tileDesc}>{d.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <div className={styles.ctaBlock}>
        <div className={styles.ctaBlockText}>
          <h3>Začněte bez instalace</h3>
          <p>Stačí vytvořit účet a&nbsp;ihned můžete vystavit první předávací protokol, fakturu nebo zápis do deníku.</p>
        </div>
        <Button variant="primary" size="lg" onClick={enterApp}>
          {isAuthenticated ? 'Přejít do aplikace' : 'Vstoupit do aplikace'}
        </Button>
      </div>

      <footer className={styles.footer}>
        <div>© {year} sitedoc.cz · Stavební dokumentace online</div>
        <div className={styles.footerLinks}>
          <Link to="/ochrana-osobnich-udaju">Ochrana osobních údajů</Link>
          <Link to="/smazani-uctu">Smazání účtu</Link>
        </div>
        <div className={styles.footerMeta}>v2 · A2 Field soft</div>
        <SupportQR variant="landing" size={72} />
      </footer>
    </div>
  );
}
